import { motion } from "framer-motion";
import { useEffect, useState } from "react";
import axios from "axios";
import { PlayCircle, ArrowRight, ArrowDown, Headphones, GraduationCap, Users, Target, Activity } from "lucide-react";
import { useLanguage } from "@/contexts/LanguageContext";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Studio portrait that matches the dark editorial mood
const PORTRAIT =
  "https://images.unsplash.com/photo-1500648767791-00dcc994a43e?auto=format&fit=crop&w=1400&q=80";

const PILLAR_ICONS = [Headphones, GraduationCap, Users, Target];

const fade = {
  hidden: { opacity: 0, y: 24 },
  show: (i = 0) => ({
    opacity: 1,
    y: 0,
    transition: { duration: 0.7, delay: i * 0.08, ease: [0.22, 1, 0.36, 1] },
  }),
};

export default function Hero() {
  const { t, lang } = useLanguage();
  const [liveTotal, setLiveTotal] = useState(null);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API}/selftest/stats`);
        setLiveTotal(res.data?.total ?? 0);
      } catch {
        setLiveTotal(null);
      }
    })();
  }, []);

  const liveLabel =
    lang === "de"
      ? `Bereits ${liveTotal ?? 0} Menschen haben den Selbsttest gemacht`
      : `${liveTotal ?? 0} people have already taken the self-test`;

  return (
    <section
      id="top"
      data-testid="hero-section"
      className="relative pt-28 md:pt-36 pb-16 md:pb-20 overflow-hidden bg-[#0A0A0A] noise-overlay"
    >
      {/* Decorative giant ! mark */}
      <div
        aria-hidden="true"
        className="hero-mark hidden md:block right-[-4%] top-12 text-[60vh]"
      >
        !
      </div>

      <div className="max-w-7xl mx-auto px-5 lg:px-10 relative">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          {/* Left — text */}
          <div className="lg:col-span-7 relative z-10">
            <motion.p
              initial="hidden"
              animate="show"
              custom={0}
              variants={fade}
              data-testid="hero-eyebrow"
              className="overline"
            >
              {t.hero.eyebrow}
            </motion.p>

            <motion.h1
              data-testid="hero-headline"
              className="font-display uppercase mt-6 leading-[0.9] tracking-tight text-[44px] sm:text-6xl md:text-7xl lg:text-[96px]"
            >
              <motion.span
                initial="hidden"
                animate="show"
                custom={1}
                variants={fade}
                className="block text-white"
              >
                {t.hero.titleLine1}
              </motion.span>
              <motion.span
                initial="hidden"
                animate="show"
                custom={2}
                variants={fade}
                className="block text-white relative"
              >
                {t.hero.titleLine2}
                <span className="block w-24 h-[3px] bg-redflag mt-3" />
              </motion.span>
            </motion.h1>

            <motion.p
              initial="hidden"
              animate="show"
              custom={3}
              variants={fade}
              data-testid="hero-subtitle"
              className="mt-7 text-base md:text-lg text-white/70 leading-relaxed max-w-xl"
            >
              {t.hero.subtitle}
            </motion.p>

            <motion.div
              initial="hidden"
              animate="show"
              custom={4}
              variants={fade}
              className="mt-9 flex flex-col sm:flex-row gap-3"
            >
              <a
                href="#podcast"
                data-testid="hero-cta-primary"
                className="btn-primary inline-flex items-center gap-3 px-6 py-4 font-medium uppercase tracking-wide text-sm"
              >
                <PlayCircle size={20} />
                {t.hero.ctaPrimary}
              </a>
              <a
                href="#categories"
                data-testid="hero-cta-secondary"
                className="btn-ghost inline-flex items-center gap-3 px-6 py-4 font-medium uppercase tracking-wide text-sm"
              >
                {t.hero.ctaSecondary}
                <ArrowRight size={18} />
              </a>
            </motion.div>

            {liveTotal !== null && liveTotal > 0 && (
              <motion.div
                initial={{ opacity: 0, y: 8 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.6, delay: 0.5 }}
                data-testid="hero-live-counter"
                className="mt-6 inline-flex items-center gap-2 font-mono-plex text-[11px] uppercase tracking-widest text-white/55"
              >
                <Activity size={12} className="text-redflag live-dot" />
                {liveLabel}
              </motion.div>
            )}
          </div>

          {/* Right — portrait card */}
          <motion.div
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 0.8, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="lg:col-span-5 relative"
          >
            <div className="relative aspect-[4/5] w-full max-w-md mx-auto overflow-hidden border border-white/10">
              <img
                src={PORTRAIT}
                alt="Studio portrait"
                className="w-full h-full object-cover grayscale-img"
                loading="eager"
              />
              <div className="absolute inset-0 bg-gradient-to-t from-[#0A0A0A]/80 via-transparent to-transparent" />
              <div className="absolute top-4 left-4 flex items-center gap-2 font-mono-plex text-[11px] uppercase tracking-widest text-white/85">
                <span className="h-2 w-2 rounded-full bg-redflag live-dot" />
                On Air · Mo 06:00
              </div>
              <div className="absolute bottom-5 left-5 right-5">
                <div className="font-mono-plex text-[10px] uppercase tracking-widest text-white/60">
                  Aktuelle Folge
                </div>
                <div className="mt-1 font-display uppercase text-xl text-white">
                  Die 10 grössten Red Flags
                </div>
              </div>
            </div>
          </motion.div>
        </div>

        {/* Bottom pillar strip */}
        <div className="mt-16 md:mt-20 grid grid-cols-2 md:grid-cols-4 gap-px bg-white/10 border border-white/10">
          {t.hero.pillars.map((p, i) => {
            const Icon = PILLAR_ICONS[i];
            return (
              <motion.div
                key={i}
                initial={{ opacity: 0, y: 14 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.5, delay: i * 0.07 }}
                data-testid={`hero-pillar-${i}`}
                className="bg-[#0F0F0F] p-6 md:p-7"
              >
                <Icon size={22} className="text-redflag" strokeWidth={1.6} />
                <div className="mt-3 font-display uppercase text-lg md:text-xl text-white">
                  {p.title}
                </div>
                <p className="mt-2 text-xs md:text-sm text-white/55 leading-relaxed">
                  {p.desc}
                </p>
              </motion.div>
            );
          })}
        </div>
      </div>
    </section>
  );
}
